import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useCarrierDispatchCenters, useCreateCarrierTrip } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus, responseError } from "@/lib/api-error";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";

interface CreateTripDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dialogPresentation?: DialogProps["presentation"];
  responsiveSheetAt?: DialogProps["responsiveSheetAt"];
}

/**
 * Create an empty PLANNED trip on one of the caller's dispatch centers —
 * `POST /trips`. Color is optional; the server auto-assigns a distinct color
 * when omitted. Defaults to the active dispatch-center filter when one is set.
 */
export function CreateTripDialog({
  open,
  onOpenChange,
  dialogPresentation,
  responsiveSheetAt,
}: CreateTripDialogProps) {
  const {
    data: centersData,
    isLoading,
    error: centersError,
  } = useCarrierDispatchCenters();
  const { dispatchCenterId: scopedCenterId } = useOptionalDispatchCenterScope();
  const create = useCreateCarrierTrip();
  const [dispatchCenterId, setDispatchCenterId] = useState<string>("");
  const [color, setColor] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  const centers = useMemo(
    () => (centersData ?? []).filter((c) => c.isActive),
    [centersData]
  );

  // Seed from the active filter (or the only center) whenever the dialog opens.
  useEffect(() => {
    if (!open || dispatchCenterId) return;
    if (scopedCenterId && centers.some((c) => c.id === scopedCenterId)) {
      setDispatchCenterId(scopedCenterId);
    } else if (centers.length === 1) {
      setDispatchCenterId(centers[0].id);
    }
  }, [open, dispatchCenterId, scopedCenterId, centers]);

  const reset = () => {
    setDispatchCenterId("");
    setColor("");
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchCenterId || submitLock.current) return;
    submitLock.current = true;
    setSubmitError(null);
    try {
      await create.mutateAsync({
        dispatchCenterId,
        ...(color ? { color } : {}),
      });
      toast.success("Trip created.");
      onOpenChange(false);
      reset();
    } catch (error) {
      const message = responseError(error, "Failed to create trip.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!create.isPending) onOpenChange(nextOpen);
      }}
      title="Create trip"
      presentation={dialogPresentation}
      responsiveSheetAt={responsiveSheetAt}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="trip-center-picker"
            className="block text-sm font-medium mb-1"
          >
            Dispatch center
          </label>
          <NativeSelect
            id="trip-center-picker"
            value={dispatchCenterId}
            onChange={(e) => {
              setDispatchCenterId(e.target.value);
              setSubmitError(null);
            }}
            disabled={isLoading || create.isPending}
          >
            <option value="">Select dispatch center...</option>
            {centers.map((center) => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div>
          <label
            htmlFor="trip-color-picker"
            className="block text-sm font-medium mb-1"
          >
            Color (optional)
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="trip-color-picker"
              type="color"
              className="w-20 p-1 md:w-16"
              value={color || "#3b82f6"}
              onChange={(e) => setColor(e.target.value)}
              disabled={create.isPending}
            />
            {color && (
              <Button
                type="button"
                variant="ghost"
                size={responsive ? "touch" : "default"}
                onClick={() => setColor("")}
                disabled={create.isPending}
              >
                Use automatic color
              </Button>
            )}
          </div>
        </div>

        {centersError && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(centersError, 403)
              ? "You do not have permission to load dispatch centers."
              : "Failed to load dispatch centers. Try again."}
          </p>
        )}

        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={!dispatchCenterId || create.isPending}
          >
            {create.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
